import { IonCol, IonContent, IonGrid, IonPage, IonRow } from '@ionic/react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from '@components/CookiePolicyPage.module.css';
import Footer from '@components/Footer';
import ButtonLink from '@components/typography/ButtonLink';
import ExternalLink from '@components/typography/ExternalLink';
import Heading1 from '@components/typography/Heading1';
import Heading2 from '@components/typography/Heading2';
import Heading3 from '@components/typography/Heading3';
import {
  analyticsCookies,
  Cookie,
  displayCookieConsentBanner,
  functionalCookies,
  necessaryCookies,
  paymentCookies
} from '@utils/cookieUtils';
import { imageUrl } from '@utils/imageUtils';

import 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

declare global {
  interface Window {
    CookieConsentApi: any;
  }
}

type CookieTableProps = {
  cookies: Cookie[];
};

const CookieTable = ({ cookies }: CookieTableProps) => {
  const intl = useIntl();

  return (
    <table className={styles.cookieTable}>
      <thead>
        <tr>
          <th>{intl.formatMessage({ id: 'dictionary.cookie' })}</th>
          <th>{intl.formatMessage({ id: 'dictionary.domain' })}</th>
          <th>{intl.formatMessage({ id: 'dictionary.vendor' })}</th>
        </tr>
      </thead>
      <tbody>
        {cookies.map(cookie => (
          <tr key={cookie.cookie}>
            <td>{cookie.cookie}</td>
            <td>{cookie.domain}</td>
            <td>{cookie.vendor}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CookiePolicyPage = () => {
  const intl = useIntl();

  const showCookieDeclaration = useCallback(() => {
    displayCookieConsentBanner();
  }, []);

  return (
    <IonPage className={styles.page}>
      <IonContent>
        <IonGrid fixed>
          <IonRow>
            <IonCol className={styles.logoColumn}>
              <img
                alt={intl.formatMessage({ id: 'accessibility.altTags.purpleLogo' })}
                className={styles.logo}
                src={imageUrl('logoPurple')}
              />
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol>
              <Heading1>{intl.formatMessage({ id: 'pages.cookiePolicy.headings.title' })}</Heading1>
              <p>
                <FormattedMessage
                  id="pages.cookiePolicy.paragraphs.explainer"
                  values={{
                    explainerLink: (
                      <ExternalLink href="https://www.withgrayce.com/privacy">
                        <FormattedMessage id="pages.cookiePolicy.paragraphs.explainerLink" />
                      </ExternalLink>
                    )
                  }}
                />
              </p>

              <p>
                <FormattedMessage
                  id="pages.cookiePolicy.paragraphs.manageConsent"
                  values={{
                    manageConsentLink: (
                      <ButtonLink onClick={showCookieDeclaration}>
                        <FormattedMessage id="pages.cookiePolicy.paragraphs.manageConsentLink" />
                      </ButtonLink>
                    )
                  }}
                />
              </p>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.learnMore" />
              </p>

              <Heading2>
                <FormattedMessage id="pages.cookiePolicy.headings.whatAreCookies" />
              </Heading2>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.whatAreCookies" />
              </p>

              <Heading2>
                <FormattedMessage id="pages.cookiePolicy.headings.howDoWeUseCookies" />
              </Heading2>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.howDoWeUseCookies" />
              </p>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.moreAboutHowWeUseCookies" />
              </p>

              <Heading2>
                <FormattedMessage id="pages.cookiePolicy.headings.types.title" />
              </Heading2>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.types" />
              </p>

              <Heading3>
                <FormattedMessage id="pages.cookiePolicy.headings.types.required" />
              </Heading3>
              <CookieTable cookies={necessaryCookies} />

              <Heading3>
                <FormattedMessage id="pages.cookiePolicy.headings.types.functional" />
              </Heading3>
              <CookieTable cookies={functionalCookies} />

              <Heading3>
                <FormattedMessage id="pages.cookiePolicy.headings.types.analytics" />
              </Heading3>
              <CookieTable cookies={analyticsCookies} />

              <Heading3>
                <FormattedMessage id="pages.cookiePolicy.headings.types.payment" />
              </Heading3>
              <CookieTable cookies={paymentCookies} />

              <Heading2>
                <FormattedMessage id="pages.cookiePolicy.headings.control" />
              </Heading2>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.control.useConsentPopup" />
              </p>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.control.useManageConsentLink" />
              </p>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.control.useBrowserSettings" />
              </p>

              <p>
                <FormattedMessage
                  id="pages.cookiePolicy.paragraphs.control.manageIndividualCookies"
                  values={{
                    manageIndividualCookiesLink: (
                      <ExternalLink href="https://www.allaboutcookies.org/manage-cookies/">
                        <FormattedMessage id="dictionary.clickHere" />
                      </ExternalLink>
                    )
                  }}
                />
              </p>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.control.blockEffects" />
              </p>

              <p>
                <FormattedMessage id="pages.cookiePolicy.paragraphs.control.useDoNotTrack" />
              </p>
            </IonCol>
          </IonRow>
        </IonGrid>
        <Footer />
      </IonContent>
    </IonPage>
  );
};

export default CookiePolicyPage;
